import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { StepLayout } from '../StepLayout';
import { AnimatedWordExample } from '../AnimatedWordExample';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useStore } from '@/app/StoreContext';
import { api } from '@/api';
import { normalizeUserInput, yoToE } from '@/domain/normalize';
import { containsInOrder } from '@/domain/letterOrder';
import { score } from '@/domain/scoring';
import { TUTORIAL_LETTERS } from '../constants';
import { SANDBOX_WORDS_KOT } from '../sandboxDictionary';
import { ru } from '@/i18n/ru';
import styles from './Step6Sandbox.module.css';

type Check =
  | { state: 'empty' }
  | { state: 'too-short' }
  | { state: 'wrong-order' }
  | { state: 'not-in-dict' }
  | { state: 'valid'; word: string; points: number };

function check(raw: string): Check {
  const norm = yoToE(normalizeUserInput(raw));
  if (!norm) return { state: 'empty' };
  if (norm.length < 4) return { state: 'too-short' };
  if (!containsInOrder(norm, TUTORIAL_LETTERS)) return { state: 'wrong-order' };
  if (!SANDBOX_WORDS_KOT.has(norm)) return { state: 'not-in-dict' };
  const r = score(norm, TUTORIAL_LETTERS);
  return { state: 'valid', word: norm, points: r ? r.rawScore : 0 };
}

export const Step6Sandbox = observer(function Step6Sandbox() {
  const { onboarding } = useStore();
  const [value, setValue] = useState('');
  const nav = useNavigate();
  const [starting, setStarting] = useState(false);
  const result = check(value);

  const startSolo = async () => {
    setStarting(true);
    try {
      const room = await api.rooms.create({
        mode: 'Solo',
        roundDurationSec: 90,
        totalRounds: 1,
        speedBonus: false,
        progressVisibility: 'Hidden',
        isPublic: false,
      });
      onboarding.close();
      nav(`/r/${room.code}`);
    } finally {
      setStarting(false);
    }
  };

  const feedback = () => {
    switch (result.state) {
      case 'too-short':
        return <p className={styles.err}>{ru.onboarding.step6.errTooShort}</p>;
      case 'wrong-order':
        return <p className={styles.err}>{ru.onboarding.step6.errWrongOrder}</p>;
      case 'not-in-dict':
        return <p className={styles.hint}>{ru.onboarding.step6.hintNotInDict}</p>;
      case 'valid':
        return <AnimatedWordExample word={result.word} letters={TUTORIAL_LETTERS} />;
      default:
        return null;
    }
  };

  return (
    <StepLayout title={ru.onboarding.step6.title}>
      <p>{ru.onboarding.step6.prompt}</p>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={ru.onboarding.step6.placeholder}
        autoFocus
        aria-label={ru.onboarding.step6.inputLabel}
      />
      <div className={styles.feedback} aria-live="polite">{feedback()}</div>
      <p className={styles.examples}>{ru.onboarding.step6.hints}</p>
      <div className={styles.cta}>
        <Button onClick={startSolo} loading={starting}>{ru.onboarding.step6.playSolo}</Button>
        <Button variant="ghost" onClick={() => onboarding.close()}>{ru.onboarding.step6.close}</Button>
      </div>
    </StepLayout>
  );
});
